import { serializeFilters } from "utils/filters";
import type AuthConnector from "../data/authConnector";
import type { IPagedResults, IPagination } from "../data/types";
import ExternalUser from "../entities/externalUser";
import Invitation from "../entities/invitation";
import User from "../entities/user";
import UserRole from "../entities/userRole";
import type EventBus from "../services/eventBus";
import RepositoryBase from "./repositoryBase";
import type { IDeleteRepository, IListRepository } from "./types";

export type ProviderKind = "INTERNAL" | "EXTERNAL";

export default class UserRepository
  extends RepositoryBase<AuthConnector>
  implements IListRepository<User>, IDeleteRepository<User>
{
  constructor(protected apiFactory: ReturnType<typeof AuthConnector.Factory>, protected eventBus: EventBus) {
    super(apiFactory, eventBus);
  }

  get(userId: string): Promise<User | ExternalUser> {
    return this.callApi(api => api.one("users", userId).getEntity(User));
  }

  getAll(pagination: IPagination, filter?: User): Promise<IPagedResults<User | ExternalUser>> {
    return this.callApi(api =>
      api.all("user_search").postPagedEntities(serializeFilters({ ...filter, ...pagination }, false), User)
    );
  }

  create(user: User) {
    return this.callApi(api => api.all("users").postEntity(user, User));
  }

  createExternal(user: ExternalUser) {
    return this.callApi(api => api.all("external_users").postEntity<ExternalUser>(user, ExternalUser));
  }

  update(user: User) {
    return this.callApi(api => api.one("users", user.id).putEntity(user, User));
  }

  delete(user: User) {
    return this.callApi(api => api.one("users", user.id).delete(user));
  }

  getAllRoles(userId: string) {
    return this.callApi(api => api.one("users", userId).all("roles").getEntities<UserRole>(UserRole));
  }

  addRole(user: User, roleId: string) {
    return this.callApi(api => api.one("users", user.id).all("roles").post({ roleId }), {
      ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }),
    });
  }

  deleteRole(user: User, userRole: UserRole) {
    return this.callApi(api => api.one("users", user.id).one("roles", userRole.id).delete({}));
  }

  activationEmail(user: User) {
    return this.callApi(api => api.one("users", user.id).all("activation_email").postOnly({}));
  }

  extendInvitation(user: User, expiresAt: Date) {
    return this.callApi(api => api.one("external_users", user.id).all("invitation").putEntity({ expiresAt }, Invitation));
  }

  getAllIdentityProviders(user: User) {
    return this.callApi(api => api.one("external_users", user.id).all("allowed_providers").get<{ items: string[] }>());
  }

  updateAllowedIdentityProviders(user: User, allowedProviders: string[]) {
    return this.callApi(api => api.one("external_users", user.id).all("allowed_providers").put({ items: allowedProviders }));
  }
}
